import "bootstrap/dist/css/bootstrap.min.css";
import {
  Calendar,
  Filter,
  Maximize,
  Minimize,
  Package,
  Plus,
  RotateCcw,
  Search,
  Star,
} from "lucide-react";
import React, { useState } from "react";

const KanbanBoard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCompactView, setIsCompactView] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // 12 colunas representando todas as fases do projeto
  const columns = [
    {
      id: "solicitacao-criada",
      title: "Solicitação Criada",
      shortTitle: "Criada",
      icon: "📝",
      color: "secondary",
    },
    {
      id: "aguardando-aprovacao",
      title: "Aguardando Aprovação",
      shortTitle: "Ag. Aprovação",
      icon: "⏳",
      color: "warning",
    },
    {
      id: "aprovado-orcamento",
      title: "Aprovado p/ Orçamento",
      shortTitle: "Aprovado",
      icon: "✅",
      color: "info",
    },
    {
      id: "coletando-orcamentos",
      title: "Coletando Orçamentos",
      shortTitle: "Orçamentos",
      icon: "📊",
      color: "primary",
    },
    {
      id: "analise-propostas",
      title: "Análise de Propostas",
      shortTitle: "Análise",
      icon: "🔍",
      color: "primary",
    },
    {
      id: "aprovacao-final",
      title: "Aprovação Final",
      shortTitle: "Ap. Final",
      icon: "🎯",
      color: "warning",
    },
    {
      id: "pedido-compra",
      title: "Pedido de Compra",
      shortTitle: "Pedido",
      icon: "🛒",
      color: "info",
    },
    {
      id: "aguardando-fornecedor",
      title: "Aguardando Fornecedor",
      shortTitle: "Ag. Fornec.",
      icon: "⏱️",
      color: "warning",
    },
    {
      id: "em-producao",
      title: "Em Produção",
      shortTitle: "Produção",
      icon: "⚙️",
      color: "primary",
    },
    {
      id: "em-transporte",
      title: "Em Transporte",
      shortTitle: "Transporte",
      icon: "🚛",
      color: "info",
    },
    {
      id: "entregue",
      title: "Entregue",
      shortTitle: "Entregue",
      icon: "📦",
      color: "success",
    },
    {
      id: "finalizado",
      title: "Finalizado/Instalado",
      shortTitle: "Finalizado",
      icon: "🏁",
      color: "success",
    },
  ];

  // Mock data distribuído pelas 12 colunas com informações completas
  const mockCards = {
    "solicitacao-criada": [
      {
        id: "SPM-3001",
        title: "Material elétrico para obra NDJ",
        description: "Lista completa de materiais elétricos",
        assignee: { name: "João Silva", avatar: "JS", color: "bg-primary" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "alta",
        dueDate: "2025-07-15",
        budget: "R$ 15.000,00",
        supplier: "A definir",
        category: "Material Elétrico",
        location: "Obra NDJ - Setor A",
        requestedBy: "Eng. Pedro Santos",
        department: "Obras",
        specifications: "Cabos 2.5mm, disjuntores 20A, tomadas padrão NBR",
        urgency: "Normal",
        approvals: ["Engenharia: Pendente", "Financeiro: Pendente"],
      },
      {
        id: "SPM-3002",
        title: "Cimento para fundação",
        description: "50 sacos de cimento CP32",
        assignee: { name: "Maria Santos", avatar: "MS", color: "bg-success" },
        buyer: { name: "Ana Compras", avatar: "AC", color: "bg-info" },
        priority: "media",
        dueDate: "2025-07-20",
        budget: "R$ 2.500,00",
        supplier: "A definir",
        category: "Construção Civil",
        location: "Obra Principal - Fundação",
        requestedBy: "Mestre José",
        department: "Obras",
        specifications: "Cimento CP32, sacos de 50kg",
        urgency: "Normal",
        approvals: ["Engenharia: Pendente"],
      },
    ],
    "aprovado-orcamento": [
      {
        id: "SPM-2555",
        title: "Placas ISOPOR",
        description: "Placas para isolamento térmico",
        assignee: { name: "Ana Lima", avatar: "AL", color: "bg-info" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "media",
        dueDate: "2025-07-18",
        budget: "R$ 3.200,00",
        supplier: "Isolamentos Brasil",
        category: "Isolamento Térmico",
        location: "Prédio Principal",
        requestedBy: "Eng. Ana Lima",
        department: "Manutenção",
        specifications: "Placas ISOPOR 50mm, densidade 20kg/m³",
        urgency: "Normal",
        approvals: ["Engenharia: Aprovado", "Financeiro: Aprovado"],
      },
    ],
    "coletando-orcamentos": [
      {
        id: "SPM-1594",
        title: "Quartzito São Tomé",
        description: "Pedras retangulares amarelas para auditório",
        assignee: { name: "Carlos Mendes", avatar: "CM", color: "bg-danger" },
        buyer: { name: "Ana Compras", avatar: "AC", color: "bg-info" },
        priority: "alta",
        dueDate: "2025-06-02",
        budget: "R$ 18.000,00",
        supplier: "Mineração Pedras Nobres",
        category: "Acabamento",
        location: "Auditório Principal",
        requestedBy: "Arq. Carlos Mendes",
        department: "Arquitetura",
        specifications: "Quartzito amarelo, formato retangular 60x30cm",
        urgency: "Alta",
        approvals: ["Arquitetura: Aprovado", "Financeiro: Aprovado"],
      },
      {
        id: "SPM-2271",
        title: "Mão de obra civil",
        description: "Processo de concorrência para obras",
        assignee: { name: "Luciana Rocha", avatar: "LR", color: "bg-primary" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "alta",
        dueDate: "2025-07-20",
        budget: "R$ 45.000,00",
        supplier: "Em cotação",
        category: "Mão de Obra",
        location: "Várias obras",
        requestedBy: "Eng. Luciana Rocha",
        department: "Obras",
        specifications: "Pedreiros, serventes, eletricistas",
        urgency: "Alta",
        approvals: ["Engenharia: Aprovado", "RH: Aprovado"],
      },
    ],
    "analise-propostas": [
      {
        id: "SPM-2596",
        title: "Esquadria alumínio Guarita",
        description: "Análise de propostas recebidas",
        assignee: { name: "Roberto Silva", avatar: "RS", color: "bg-success" },
        buyer: { name: "Ana Compras", avatar: "AC", color: "bg-info" },
        priority: "alta",
        dueDate: "2025-06-27",
        budget: "R$ 12.000,00",
        supplier: "Alumínio Premium (melhor proposta)",
        category: "Esquadrias",
        location: "Guarita Principal",
        requestedBy: "Seg. Roberto Silva",
        department: "Segurança",
        specifications: "Perfil de alumínio, vidros temperados",
        urgency: "Alta",
        approvals: ["Segurança: Aprovado", "Financeiro: Em análise"],
      },
    ],
    "aprovacao-final": [
      {
        id: "SPM-2252",
        title: "Tintas para manutenção",
        description: "Aprovação final do fornecedor",
        assignee: { name: "Sandra Costa", avatar: "SC", color: "bg-warning" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "media",
        dueDate: "2025-07-01",
        budget: "R$ 4.500,00",
        supplier: "Tintas & Cores Ltda",
        category: "Pintura",
        location: "Prédios A, B e C",
        requestedBy: "Maint. Sandra Costa",
        department: "Manutenção",
        specifications: "Tinta acrílica, primer, verniz",
        urgency: "Normal",
        approvals: [
          "Manutenção: Aprovado",
          "Financeiro: Aprovado",
          "Diretor: Pendente",
        ],
      },
    ],
    "pedido-compra": [
      {
        id: "SPM-2581",
        title: "Macro e micro fibra",
        description: "Pedido de compra gerado",
        assignee: { name: "Fernando Dias", avatar: "FD", color: "bg-info" },
        buyer: { name: "Ana Compras", avatar: "AC", color: "bg-info" },
        priority: "alta",
        dueDate: "2025-06-26",
        budget: "R$ 8.200,00",
        supplier: "Fibras Industriais S/A",
        category: "Materiais Especiais",
        location: "Estacionamento - Estrutura",
        requestedBy: "Eng. Fernando Dias",
        department: "Engenharia",
        specifications: "Fibra de aço para concreto",
        urgency: "Alta",
        approvals: ["Todas aprovadas - PC #45821 gerado"],
      },
    ],
    "aguardando-fornecedor": [
      {
        id: "SPM-2534",
        title: "Aço para fundação",
        description: "Aguardando confirmação do fornecedor",
        assignee: { name: "Paula Martins", avatar: "PM", color: "bg-danger" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "urgente",
        dueDate: "2025-06-25",
        budget: "R$ 22.000,00",
        supplier: "Aços & Metais Industrial",
        category: "Estrutural",
        location: "Fundação - Bloco C",
        requestedBy: "Eng. Paula Martins",
        department: "Estrutural",
        specifications: "Barras de aço CA-50, diâmetros variados",
        urgency: "Urgente",
        approvals: ["PC enviado - Aguardando aceite do fornecedor"],
      },
    ],
    "em-producao": [
      {
        id: "SPM-2502",
        title: "Mini guias de concreto",
        description: "Em produção na fábrica",
        assignee: { name: "José Ferreira", avatar: "JF", color: "bg-primary" },
        buyer: { name: "Ana Compras", avatar: "AC", color: "bg-info" },
        priority: "media",
        dueDate: "2025-07-10",
        budget: "R$ 6.800,00",
        supplier: "Pré-Moldados Cidade",
        category: "Pré-Moldados",
        location: "Estacionamento e vias",
        requestedBy: "Urb. José Ferreira",
        department: "Urbanismo",
        specifications: "Mini guias 85x15cm, concreto FCK 25",
        urgency: "Normal",
        approvals: ["Produção iniciada - Previsão 15 dias"],
      },
    ],
    "em-transporte": [
      {
        id: "SPM-2505",
        title: "Cintos de segurança",
        description: "A caminho do local da obra",
        assignee: { name: "Marcos Souza", avatar: "MS", color: "bg-success" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "alta",
        dueDate: "2025-07-08",
        budget: "R$ 1.200,00",
        supplier: "Segurança Total EPI",
        category: "EPI",
        location: "Almoxarifado Central",
        requestedBy: "Seg. Marcos Souza",
        department: "Segurança do Trabalho",
        specifications: "Cintos tipo paraquedista, CA válido",
        urgency: "Alta",
        approvals: ["Enviado - Previsão chegada: 08/07"],
      },
    ],
    entregue: [
      {
        id: "SPM-2523",
        title: "Materiais elétricos NDC",
        description: "Entregue e conferido",
        assignee: { name: "Laura Mendes", avatar: "LM", color: "bg-info" },
        buyer: { name: "Ana Compras", avatar: "AC", color: "bg-info" },
        priority: "baixa",
        dueDate: "2025-07-05",
        budget: "R$ 3.400,00",
        supplier: "Elétrica Moderna",
        category: "Material Elétrico",
        location: "Prédio NDC - 2º andar",
        requestedBy: "Elet. Laura Mendes",
        department: "Elétrica",
        specifications: "Cabos, disjuntores, tomadas",
        urgency: "Baixa",
        approvals: ["Entregue em 05/07 - NF: 12457"],
      },
    ],
    finalizado: [
      {
        id: "SPM-2528",
        title: "Parafusos e arruelas",
        description: "Instalado e finalizado",
        assignee: { name: "Ricardo Lima", avatar: "RL", color: "bg-success" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "baixa",
        dueDate: "2025-07-03",
        budget: "R$ 800,00",
        supplier: "Parafusos & CIA",
        category: "Fixação",
        location: "Estacionamento - Sinalização",
        requestedBy: "Mont. Ricardo Lima",
        department: "Montagem",
        specifications: "Parafusos inox, arruelas, porcas",
        urgency: "Baixa",
        approvals: ["Instalado e finalizado - 03/07"],
      },
    ],
    "aguardando-aprovacao": [
      {
        id: "SPM-2420",
        title: "Materiais para estacionamento",
        description: "Lista de materiais para obra do estacionamento",
        assignee: { name: "Pedro Costa", avatar: "PC", color: "bg-warning" },
        buyer: { name: "Carlos Compras", avatar: "CC", color: "bg-success" },
        priority: "alta",
        dueDate: "2025-07-12",
        budget: "R$ 25.000,00",
        supplier: "Construtora Alpha",
        category: "Infraestrutura",
        location: "Estacionamento Principal",
        requestedBy: "Arq. Amanda Silva",
        department: "Projetos",
        specifications: "Blocos de concreto, tinta asfáltica, sinalização",
        urgency: "Alta",
        approvals: [
          "Engenharia: Aprovado",
          "Financeiro: Pendente",
          "Diretor: Pendente",
        ],
      },
    ],
  };

  // Função para abrir o chat
  const openChat = (card) => {
    setSelectedCard(card);
    // Inicializar chat se não existir
    if (!chatMessages[card.id]) {
      setChatMessages((prev) => ({
        ...prev,
        [card.id]: mockChatHistory[card.id] || [],
      }));
    }
  };

  // Função para enviar mensagem
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedCard) return;

    const userMessage = {
      id: Date.now(),
      sender: "Você",
      message: newMessage,
      time: new Date().toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      isUser: true,
    };

    // Adicionar mensagem do usuário
    setChatMessages((prev) => ({
      ...prev,
      [selectedCard.id]: [...(prev[selectedCard.id] || []), userMessage],
    }));

    setNewMessage("");

    // Simular resposta automática do comprador usando Claude
    try {
      const prompt = `Você é ${selectedCard.buyer.name}, um comprador experiente. O usuário ${selectedCard.assignee.name} acabou de enviar: "${newMessage}". 

Contexto do item:
- ID: ${selectedCard.id}
- Título: ${selectedCard.title}
- Orçamento: ${selectedCard.budget}
- Fornecedor: ${selectedCard.supplier}
- Prazo: ${selectedCard.dueDate}

Responda como um comprador profissional, de forma breve e útil, em português brasileiro. Use emojis ocasionalmente.`;

      const response = await window.claude.complete(prompt);

      // Adicionar resposta do comprador após 2 segundos
      setTimeout(() => {
        const buyerMessage = {
          id: Date.now() + 1,
          sender: selectedCard.buyer.name,
          message: response,
          time: new Date().toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          isUser: false,
        };

        setChatMessages((prev) => ({
          ...prev,
          [selectedCard.id]: [...(prev[selectedCard.id] || []), buyerMessage],
        }));
      }, 2000);
    } catch (error) {
      // Resposta padrão se a API falhar
      setTimeout(() => {
        const buyerMessage = {
          id: Date.now() + 1,
          sender: selectedCard.buyer.name,
          message: "Entendi! Vou verificar isso e te retorno em breve. 👍",
          time: new Date().toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          isUser: false,
        };

        setChatMessages((prev) => ({
          ...prev,
          [selectedCard.id]: [...(prev[selectedCard.id] || []), buyerMessage],
        }));
      }, 2000);
    }
  };

  // Mock de conversas para cada card
  const mockChatHistory = {
    "SPM-3001": [
      {
        id: 1,
        sender: "João Silva",
        message:
          "Oi Carlos! Preciso urgente desses materiais elétricos para a obra NDJ",
        time: "09:15",
        isUser: true,
      },
      {
        id: 2,
        sender: "Carlos Compras",
        message:
          "Oi João! Vi a solicitação aqui. Você tem a lista detalhada dos materiais?",
        time: "09:18",
        isUser: false,
      },
      {
        id: 3,
        sender: "João Silva",
        message:
          "Sim! São cabos 2.5mm (100m), disjuntores 20A (12 unidades) e tomadas padrão NBR (20 unidades)",
        time: "09:20",
        isUser: true,
      },
      {
        id: 4,
        sender: "Carlos Compras",
        message:
          "Perfeito! Vou pesquisar com 3 fornecedores. Orçamento previsto de R$ 15.000. Tudo bem?",
        time: "09:25",
        isUser: false,
      },
      {
        id: 5,
        sender: "João Silva",
        message: "Ótimo! Quando você acha que consegue os orçamentos?",
        time: "09:27",
        isUser: true,
      },
      {
        id: 6,
        sender: "Carlos Compras",
        message:
          "Até quinta-feira eu tenho retorno. Vou colocar no sistema como urgente 👍",
        time: "09:30",
        isUser: false,
      },
    ],
    "SPM-2420": [
      {
        id: 1,
        sender: "Pedro Costa",
        message: "Carlos, essa obra do estacionamento é prioridade máxima!",
        time: "08:00",
        isUser: true,
      },
      {
        id: 2,
        sender: "Carlos Compras",
        message:
          "Entendi Pedro! Já estou com 3 orçamentos em mãos. Construtora Alpha deu o melhor preço.",
        time: "08:05",
        isUser: false,
      },
      {
        id: 3,
        sender: "Pedro Costa",
        message: "Qual o valor? E a qualidade dos materiais?",
        time: "08:06",
        isUser: true,
      },
      {
        id: 4,
        sender: "Carlos Compras",
        message:
          "R$ 25.000 total. Blocos de concreto classe A, tinta asfáltica importada. Qualidade top!",
        time: "08:10",
        isUser: false,
      },
      {
        id: 5,
        sender: "Pedro Costa",
        message: "Excelente! Quando posso enviar para aprovação final?",
        time: "08:12",
        isUser: true,
      },
      {
        id: 6,
        sender: "Carlos Compras",
        message:
          "Já está no sistema aguardando aprovação do financeiro e diretor. Prazo de entrega: 10 dias úteis",
        time: "08:15",
        isUser: false,
      },
      {
        id: 7,
        sender: "Pedro Costa",
        message:
          "Vou falar com a Amanda para acelerar as aprovações. Obrigado! 🚀",
        time: "08:17",
        isUser: true,
      },
    ],
    "SPM-2555": [
      {
        id: 1,
        sender: "Ana Lima",
        message:
          "Carlos, preciso dessas placas de ISOPOR para o isolamento térmico",
        time: "14:30",
        isUser: true,
      },
      {
        id: 2,
        sender: "Carlos Compras",
        message:
          "Oi Ana! Já tenho um fornecedor aprovado para isso. Isolamentos Brasil tem bom preço",
        time: "14:35",
        isUser: false,
      },
      {
        id: 3,
        sender: "Ana Lima",
        message: "Perfeito! Qual a especificação que eles têm disponível?",
        time: "14:36",
        isUser: true,
      },
      {
        id: 4,
        sender: "Carlos Compras",
        message:
          "Placas de 50mm, densidade 20kg/m³. Exatamente o que você pediu. R$ 3.200 total",
        time: "14:40",
        isUser: false,
      },
    ],
    "SPM-1594": [
      {
        id: 1,
        sender: "Carlos Mendes",
        message:
          "Ana, esse quartzito para o auditório é bem específico. Conseguiu cotação?",
        time: "10:00",
        isUser: true,
      },
      {
        id: 2,
        sender: "Ana Compras",
        message:
          "Carlos! Estou com 3 cotações. A Mineração Pedras Nobres tem o melhor material",
        time: "10:15",
        isUser: false,
      },
      {
        id: 3,
        sender: "Carlos Mendes",
        message:
          "E o prazo de entrega? O auditório precisa ficar pronto até final do mês",
        time: "10:17",
        isUser: true,
      },
      {
        id: 4,
        sender: "Ana Compras",
        message:
          "Eles garantem entrega em 15 dias. Quartzito amarelo São Tomé, formato 60x30cm. R$ 18.000",
        time: "10:20",
        isUser: false,
      },
      {
        id: 5,
        sender: "Carlos Mendes",
        message: "Excelente! Pode fechar. Vou aprovar aqui no sistema",
        time: "10:22",
        isUser: true,
      },
    ],
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgente":
        return "danger";
      case "alta":
        return "warning";
      case "media":
        return "info";
      case "baixa":
        return "secondary";
      default:
        return "light";
    }
  };

  const getTotalCards = () => {
    return Object.values(mockCards).reduce(
      (total, cards) => total + cards.length,
      0
    );
  };

  const getColumnWidth = () => {
    return isCompactView ? "200px" : "280px";
  };

  // Função para mostrar tooltip
  const showTooltip = (card, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.right + 10,
      y: rect.top,
    });
    setHoveredCard(card);
  };

  // Função para esconder tooltip
  const hideTooltip = () => {
    setHoveredCard(null);
  };

  // Componente de Tooltip
  const Tooltip = ({ card, position }) => {
    if (!card) return null;

    return (
      <div
        className="position-fixed bg-white shadow-lg border rounded-3 p-3"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 1060,
          width: "280px",
          transform: "translateY(-20px)",
          transition: "all 0.2s ease",
        }}
      >
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div>
            <span
              className={`badge bg-${getPriorityColor(
                card.priority
              )} bg-opacity-15 text-${getPriorityColor(card.priority)} me-2`}
            >
              {card.id}
            </span>
            <span
              className={`badge bg-${getPriorityColor(
                card.priority
              )} bg-opacity-20 text-${getPriorityColor(card.priority)}`}
            >
              {card.priority.toUpperCase()}
            </span>
          </div>
          <div
            className={
              card.assignee.color +
              " rounded-circle d-flex align-items-center justify-content-center"
            }
            style={{ width: "24px", height: "24px" }}
          >
            <span className="text-white fw-bold" style={{ fontSize: "10px" }}>
              {card.assignee.avatar}
            </span>
          </div>
        </div>

        <h6
          className="fw-bold mb-2"
          style={{ fontSize: "13px", lineHeight: "1.3" }}
        >
          {card.title}
        </h6>

        <div className="row g-2 mb-2">
          <div className="col-6">
            <div className="bg-light rounded p-2">
              <small
                className="text-muted d-block"
                style={{ fontSize: "10px" }}
              >
                💰 Orçamento
              </small>
              <small className="fw-bold text-success">{card.budget}</small>
            </div>
          </div>
          <div className="col-6">
            <div className="bg-light rounded p-2">
              <small
                className="text-muted d-block"
                style={{ fontSize: "10px" }}
              >
                📅 Prazo
              </small>
              <small className="fw-bold">
                {new Date(card.dueDate).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                })}
              </small>
            </div>
          </div>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block" style={{ fontSize: "10px" }}>
            🏢 Fornecedor
          </small>
          <small className="fw-medium">{card.supplier}</small>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block" style={{ fontSize: "10px" }}>
            👤 Solicitante
          </small>
          <small className="fw-medium">{card.requestedBy}</small>
        </div>

        <div className="border-top pt-2 mt-2">
          <small className="text-primary" style={{ fontSize: "10px" }}>
            💬 Clique para abrir chat e ver mais detalhes
          </small>
        </div>
      </div>
    );
  };

  return (
    <div
      className="vh-100 d-flex flex-column"
      style={{ backgroundColor: "#f8f9fa" }}
    >
      {/* Header Compacto */}
      <div className="bg-white border-bottom shadow-sm px-4 py-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="bg-primary rounded me-3 d-flex align-items-center justify-content-center"
              style={{ width: "32px", height: "32px" }}
            >
              <Package className="text-white" size={16} />
            </div>
            <div className="me-4">
              <h5 className="mb-0 fw-bold">
                Cronograma de Compras - Suprimentos
              </h5>
              <small className="text-muted">
                {getTotalCards()} itens em {columns.length} fases
              </small>
            </div>
            <Star className="text-warning me-2" size={16} />
            <RotateCcw className="text-muted" size={16} />
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* Botão de alternância de visualização */}
            <button
              className={`btn btn-sm ${
                isCompactView ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setIsCompactView(!isCompactView)}
              title={isCompactView ? "Expandir colunas" : "Compactar colunas"}
            >
              {isCompactView ? <Maximize size={14} /> : <Minimize size={14} />}
              <span className="ms-2">
                {isCompactView ? "Expandir" : "Compactar"}
              </span>
            </button>

            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Buscar por ID ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "250px", paddingLeft: "35px" }}
              />
              <Search
                className="position-absolute text-muted"
                size={14}
                style={{
                  top: "50%",
                  left: "10px",
                  transform: "translateY(-50%)",
                }}
              />
            </div>

            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm">
                <Filter size={14} className="me-1" />
                Filtros
              </button>
              <button className="btn btn-primary btn-sm">
                <Plus size={14} className="me-1" />
                Novo Item
              </button>
            </div>

            <div className="d-flex align-items-center">
              <div
                className="bg-warning rounded-circle me-2 d-flex align-items-center justify-content-center"
                style={{ width: "28px", height: "28px" }}
              >
                <span className="text-dark fw-bold small">U</span>
              </div>
              <span className="small fw-medium">Jennifer Onhover</span>
            </div>
          </div>
        </div>
      </div>

      {/* Kanban Board com 12 Colunas */}
      <div
        className="flex-grow-1 d-flex p-3 gap-2"
        style={{ overflowX: "auto", overflowY: "hidden" }}
      >
        {columns.map((column, index) => {
          const cards = mockCards[column.id] || [];
          const filteredCards = cards.filter(
            (card) =>
              card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
              card.id.toLowerCase().includes(searchTerm.toLowerCase())
          );

          return (
            <div
              key={column.id}
              className="bg-white rounded-3 shadow-sm border flex-shrink-0"
              style={{
                width: getColumnWidth(),
                minWidth: getColumnWidth(),
                maxHeight: "calc(100vh - 120px)",
              }}
            >
              {/* Header da Coluna */}
              <div className="p-3 border-bottom bg-light rounded-top-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="d-flex align-items-center">
                    <span className="me-2" style={{ fontSize: "14px" }}>
                      {column.icon}
                    </span>
                    <h6
                      className="mb-0 fw-bold"
                      style={{ fontSize: isCompactView ? "11px" : "13px" }}
                    >
                      {isCompactView ? column.shortTitle : column.title}
                    </h6>
                  </div>
                  <span
                    className={`badge bg-${column.color} bg-opacity-20 text-${column.color} rounded-pill`}
                    style={{ fontSize: "10px" }}
                  >
                    {filteredCards.length}
                  </span>
                </div>
                {!isCompactView && (
                  <div
                    className="small text-muted"
                    style={{ fontSize: "10px" }}
                  >
                    Fase {index + 1} de {columns.length}
                  </div>
                )}
              </div>

              {/* Cards da Coluna */}
              <div
                className="p-2"
                style={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
              >
                <div className="d-grid gap-2">
                  {filteredCards.map((card) => (
                    <div
                      key={card.id}
                      className="card border-0 shadow-sm"
                      style={{
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        fontSize: isCompactView ? "11px" : "12px",
                      }}
                      onClick={() => openChat(card)}
                      onMouseEnter={(e) => {
                        showTooltip(card, e);
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(0,0,0,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        hideTooltip();
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 6px rgba(0,0,0,0.1)";
                      }}
                    >
                      <div
                        className={`card-body ${isCompactView ? "p-2" : "p-3"}`}
                      >
                        <div className="d-flex align-items-start justify-content-between mb-2">
                          <span
                            className={`badge bg-${getPriorityColor(
                              card.priority
                            )} bg-opacity-15 text-${getPriorityColor(
                              card.priority
                            )}`}
                            style={{ fontSize: "9px", fontWeight: "600" }}
                          >
                            {card.id}
                          </span>
                          <div
                            className={
                              card.assignee.color +
                              " rounded-circle d-flex align-items-center justify-content-center"
                            }
                            style={{
                              width: isCompactView ? "18px" : "20px",
                              height: isCompactView ? "18px" : "20px",
                              minWidth: isCompactView ? "18px" : "20px",
                            }}
                          >
                            <span
                              className="text-white fw-bold"
                              style={{
                                fontSize: isCompactView ? "8px" : "9px",
                              }}
                            >
                              {card.assignee.avatar}
                            </span>
                          </div>
                        </div>

                        <h6
                          className="card-title mb-1"
                          style={{
                            fontSize: isCompactView ? "11px" : "12px",
                            lineHeight: "1.2",
                            fontWeight: "600",
                            display: "-webkit-box",
                            WebkitLineClamp: isCompactView ? 2 : 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {card.title}
                        </h6>

                        {!isCompactView && (
                          <p
                            className="text-muted mb-2"
                            style={{
                              fontSize: "10px",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {card.description}
                          </p>
                        )}

                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <Calendar
                              className="text-muted me-1"
                              size={isCompactView ? 10 : 11}
                            />
                            <small
                              className="text-muted"
                              style={{
                                fontSize: isCompactView ? "9px" : "10px",
                              }}
                            >
                              {new Date(card.dueDate).toLocaleDateString(
                                "pt-BR",
                                { day: "2-digit", month: "2-digit" }
                              )}
                            </small>
                          </div>
                          <div
                            className={`badge bg-${getPriorityColor(
                              card.priority
                            )} bg-opacity-10 text-${getPriorityColor(
                              card.priority
                            )}`}
                            style={{ fontSize: "8px" }}
                          >
                            {card.priority.toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Botão para adicionar novo item */}
                <button
                  className="btn btn-outline-secondary btn-sm w-100 mt-2"
                  style={{ fontSize: isCompactView ? "10px" : "11px" }}
                >
                  <Plus size={isCompactView ? 10 : 12} className="me-1" />
                  {isCompactView ? "+" : "Adicionar"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Indicador de scroll horizontal */}
      <div className="px-4 pb-2">
        <div className="bg-light rounded-pill" style={{ height: "4px" }}>
          <div
            className="bg-primary rounded-pill"
            style={{ height: "4px", width: "20%" }}
          ></div>
        </div>
      </div>

      {/* Tooltip */}
      <Tooltip card={hoveredCard} position={tooltipPosition} />

      {/* Modal do Chat */}
      {selectedCard && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content" style={{ height: "90vh" }}>
              <div className="modal-header bg-success text-white">
                <div className="d-flex align-items-center">
                  <div
                    className={
                      selectedCard.buyer.color +
                      " rounded-circle d-flex align-items-center justify-content-center me-3"
                    }
                    style={{ width: "40px", height: "40px" }}
                  >
                    <span className="text-white fw-bold">
                      {selectedCard.buyer.avatar}
                    </span>
                  </div>
                  <div>
                    <h5 className="modal-title mb-0">{selectedCard.title}</h5>
                    <small>Conversa com {selectedCard.buyer.name}</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedCard(null)}
                ></button>
              </div>

              <div
                className="modal-body p-0 d-flex"
                style={{ height: "calc(90vh - 120px)" }}
              >
                {/* Painel de Informações do Item */}
                <div
                  className="bg-light border-end p-4"
                  style={{ width: "350px", overflowY: "auto" }}
                >
                  <h6 className="fw-bold mb-3">📋 Informações do Item</h6>

                  <div className="mb-3">
                    <strong className="text-primary">{selectedCard.id}</strong>
                    <div className="text-muted small mt-1">
                      {selectedCard.title}
                    </div>
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-12">
                      <div className="card border-0 bg-white p-2">
                        <small className="text-muted">💰 Orçamento</small>
                        <strong className="text-success">
                          {selectedCard.budget}
                        </strong>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card border-0 bg-white p-2">
                        <small className="text-muted">📅 Prazo</small>
                        <strong>
                          {new Date(selectedCard.dueDate).toLocaleDateString(
                            "pt-BR"
                          )}
                        </strong>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="card border-0 bg-white p-2">
                        <small className="text-muted">🏢 Fornecedor</small>
                        <strong>{selectedCard.supplier}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">📝 Especificações</small>
                    <div className="bg-white rounded p-2 mt-1">
                      <small>{selectedCard.specifications}</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">📍 Local</small>
                    <div className="bg-white rounded p-2 mt-1">
                      <small>{selectedCard.location}</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">👤 Solicitante</small>
                    <div className="bg-white rounded p-2 mt-1">
                      <small>{selectedCard.requestedBy}</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">🏛️ Departamento</small>
                    <div className="bg-white rounded p-2 mt-1">
                      <small>{selectedCard.department}</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">⚡ Urgência</small>
                    <div className="bg-white rounded p-2 mt-1">
                      <span
                        className={`badge bg-${getPriorityColor(
                          selectedCard.priority
                        )}`}
                      >
                        {selectedCard.urgency}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <small className="text-muted">✅ Aprovações</small>
                    <div className="bg-white rounded p-2 mt-1">
                      {Array.isArray(selectedCard.approvals) ? (
                        selectedCard.approvals.map((approval, index) => (
                          <div key={index} className="small mb-1">
                            {approval.includes("Aprovado")
                              ? "✅"
                              : approval.includes("Pendente")
                              ? "⏳"
                              : "📋"}{" "}
                            {approval}
                          </div>
                        ))
                      ) : (
                        <small>{selectedCard.approvals}</small>
                      )}
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-grow-1 d-flex flex-column">
                  {/* Mensagens */}
                  <div
                    className="flex-grow-1 p-3"
                    style={{ overflowY: "auto", backgroundColor: "#f0f2f5" }}
                  >
                    {(chatMessages[selectedCard.id] || []).map((msg) => (
                      <div
                        key={msg.id}
                        className={`d-flex mb-3 ${
                          msg.isUser
                            ? "justify-content-end"
                            : "justify-content-start"
                        }`}
                      >
                        <div
                          className={`d-flex align-items-end ${
                            msg.isUser ? "flex-row-reverse" : "flex-row"
                          }`}
                          style={{ maxWidth: "70%" }}
                        >
                          <div
                            className={`${
                              msg.isUser
                                ? selectedCard.assignee.color
                                : selectedCard.buyer.color
                            } rounded-circle d-flex align-items-center justify-content-center me-2 ms-2`}
                            style={{
                              width: "32px",
                              height: "32px",
                              minWidth: "32px",
                            }}
                          >
                            <span
                              className="text-white fw-bold"
                              style={{ fontSize: "10px" }}
                            >
                              {msg.isUser
                                ? selectedCard.assignee.avatar
                                : selectedCard.buyer.avatar}
                            </span>
                          </div>
                          <div>
                            <div
                              className={`p-3 rounded-3 ${
                                msg.isUser
                                  ? "bg-primary text-white"
                                  : "bg-white text-dark"
                              }`}
                            >
                              <div style={{ fontSize: "14px" }}>
                                {msg.message}
                              </div>
                            </div>
                            <small
                              className="text-muted d-block mt-1"
                              style={{ fontSize: "11px" }}
                            >
                              {msg.sender} • {msg.time}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Input de Mensagem */}
                  <div className="border-top bg-white p-3">
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Digite sua mensagem..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={sendMessage}
                        disabled={!newMessage.trim()}
                      >
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;
